import React from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    description: string | React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    isLoading?: boolean;
    variant?: 'danger' | 'warning' | 'info';
    /** Desliga o botão de confirmar (ex.: campo obrigatório vazio). */
    confirmDisabled?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    isLoading = false,
    variant = 'danger',
    confirmDisabled = false,
}) => {
    if (!isOpen) return null;

    const getVariantClasses = () => {
        switch (variant) {
            case 'danger':
                return {
                    iconBg: 'bg-red-100 dark:bg-red-900/30',
                    iconColor: 'text-red-600 dark:text-red-400',
                    buttonBg: 'bg-red-600 hover:bg-red-700 text-white',
                };
            case 'warning':
                return {
                    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
                    iconColor: 'text-orange-600 dark:text-orange-400',
                    buttonBg: 'bg-orange-600 hover:bg-orange-700 text-white',
                };
            case 'info':
            default:
                return {
                    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
                    iconColor: 'text-blue-600 dark:text-blue-400',
                    buttonBg: 'bg-blue-600 hover:bg-blue-700 text-white',
                };
        }
    };

    const styles = getVariantClasses();

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl shadow-2xl transform transition-all scale-100 overflow-hidden">
                <div className="p-6">
                    <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${styles.iconBg} mb-4`}>
                        <AlertTriangle className={`h-6 w-6 ${styles.iconColor}`} aria-hidden="true" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {title}
                        </h3>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                            {description}
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-4 flex flex-row-reverse gap-2">
                    <button
                        type="button"
                        className={`inline-flex w-full justify-center rounded-lg px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto ${styles.buttonBg} disabled:opacity-50 disabled:cursor-not-allowed`}
                        onClick={onConfirm}
                        disabled={isLoading || confirmDisabled}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                        {confirmText}
                    </button>
                    <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-lg bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-300 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 sm:mt-0 sm:w-auto"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {cancelText}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmationModal;
